import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { BusinessData } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

// Create a unique, descriptive key for this query
const BUSINESS_DATA_QUERY_KEY = 'business-data'

/**
 * Fetch invoices from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessData = async (): Promise<BusinessData> => {
  const response = await APIServices.get('/administration/business')
  return response.data
}

/**
 * Custom hook for fetching invoices with filtering
 *
 * @param params - Query parameters for filtering invoices
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessDataQuery = ({
  options,
}: {
  options?: Partial<UseQueryOptions<BusinessData, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<BusinessData, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [BUSINESS_DATA_QUERY_KEY],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchBusinessData(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    businessData: queryResult.data,
    isLoadingBusinessData:
      queryResult.isLoading ||
      queryResult.isFetching ||
      queryResult.isRefetching,
    isErrorBusinessData: queryResult.isError,
    refetchBusinessData: queryResult.refetch,
  }
}

export const getBusinessDataQueryKey = () => [BUSINESS_DATA_QUERY_KEY]
